import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  fetchUserAssignedRoles,
  unassignUserFromRole,
  UserAssignedRole,
} from "services/work-security.service";
import { describePositionsError } from "features/positions/positions-errors";
import { useManagedAreasState } from "./useManagedAreas";
import IndividualRoleAdd from "./IndividualRoleAdd";

/**
 * A role's "where it comes from" tags — from a position, added individually.
 * Identical wording wherever pareigybės are listed; kept as its own component
 * so the employee card, the edit form and the internal profile can never
 * drift apart on this text again.
 */
export function RoleSourceBadges({ item }: { item: Pick<UserAssignedRole, "isFromPosition" | "fromPositions" | "isIndividual"> }) {
  const { t } = useTranslation();
  return (
    <>
      {item.isFromPosition && (
        <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800">
          {(item.fromPositions || []).length > 0
            ? t("ws_role_source_positions", { names: (item.fromPositions || []).join(", ") })
            : t("ws_role_source_positions_plain")}
        </span>
      )}
      {item.isIndividual && (
        <span className="inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-800">
          {t("ws_role_source_individual")}
        </span>
      )}
    </>
  );
}

export interface UserRolesPanelProps {
  companyId: string;
  userId: string;
  /**
   * View-only (the internal profile): drops the individual add/remove
   * control. `renderRoleExtra` is how the profile still shows its own
   * documents under each role.
   */
  readOnly?: boolean;
  /** Rendered under a role's badges — the profile's per-role document list (view/sign/download). */
  renderRoleExtra?: (item: UserAssignedRole) => React.ReactNode;
  /**
   * Controlled data. The profile keeps its own cache across tab remounts and
   * derives its "unsigned docs" tab badge from the same fetch, so it loads
   * this itself and passes the result down. Left `undefined` (the default),
   * the panel fetches its own — the employee card and the edit form.
   */
  roles?: UserAssignedRole[] | null;
  loadFailed?: boolean;
}

/**
 * An employee's effective pareigybės (work-security roles) — shared by the
 * employee edit form, the darbuotojo kortelė's «Tvarkos» tab (manage mode:
 * individual add/remove, own fetch) and the internal profile's «Roles» tab
 * (`readOnly`: no management, documents via `renderRoleExtra`, controlled
 * data). One list rendering means the role badges can't say something
 * different from one screen to the next.
 *
 * Manage mode hides itself entirely when the viewer manages no sphere and
 * the employee holds no pareigybė at all — nothing to show and nothing to
 * do. `readOnly` never hides itself; the caller (the profile) decides what
 * an empty or failed load looks like around it.
 */
export default function UserRolesPanel({
  companyId,
  userId,
  readOnly = false,
  renderRoleExtra,
  roles: rolesProp,
  loadFailed: loadFailedProp,
}: UserRolesPanelProps) {
  const { t } = useTranslation();
  const controlled = rolesProp !== undefined;
  // Read-only callers (the profile) never manage a sphere from here — skip the fetch.
  const { areas: managedAreas, loaded: areasLoaded } = useManagedAreasState(readOnly ? undefined : companyId);
  const managedAreaIds = new Set(managedAreas.map((area) => area.id));
  const [ownRoles, setOwnRoles] = useState<UserAssignedRole[] | null>(null);
  const [ownLoadFailed, setOwnLoadFailed] = useState(false);
  const [removingRoleId, setRemovingRoleId] = useState<string | null>(null);
  // Guards against a slow response for a previous employee replacing the
  // current list — the edit form reuses this component across users.
  const requestRef = useRef(0);

  const loadOwnRoles = useCallback(() => {
    const requestId = ++requestRef.current;
    fetchUserAssignedRoles(companyId, userId)
      .then((data) => {
        if (requestId !== requestRef.current) return;
        setOwnRoles(data.roles || []);
        setOwnLoadFailed(false);
      })
      .catch(() => {
        if (requestId !== requestRef.current) return;
        setOwnLoadFailed(true);
      });
  }, [companyId, userId]);

  useEffect(() => {
    if (controlled) return;
    // A new companyId/userId means a stale in-flight request too — clear the
    // previous employee's list instead of leaving it up while the new one loads.
    setOwnRoles(null);
    setOwnLoadFailed(false);
    loadOwnRoles();
    // Controlled mode (the profile) fetches on its own and passes the result down.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlled, loadOwnRoles]);

  const roles = controlled ? rolesProp ?? null : ownRoles;
  const loadFailed = controlled ? !!loadFailedProp : ownLoadFailed;

  const individualRoleIds = (roles || []).filter((item) => item.isIndividual).map((item) => item.role.id);

  const removeIndividual = async (item: UserAssignedRole) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t("ws_individual_remove_confirm", { name: item.role.name }))) return;
    setRemovingRoleId(item.role.id);
    try {
      await unassignUserFromRole(companyId, item.role.id, userId);
      toast.success(t("ws_individual_removed"));
    } catch (e: any) {
      toast.error(describePositionsError(e, t));
    } finally {
      setRemovingRoleId(null);
      loadOwnRoles();
    }
  };

  if (!readOnly) {
    // Wait for both answers before deciding: rendering and then hiding again
    // makes the section flicker in the middle of the form.
    if (!loadFailed && (!areasLoaded || roles === null)) {
      return null;
    }
    // Nothing to manage and nothing assigned: stay out of the way entirely.
    if (managedAreas.length === 0 && !loadFailed && roles !== null && roles.length === 0) {
      return null;
    }
  }

  // The sphere badge only earns its place once it distinguishes something —
  // a single-instruction-area company (today's default) never sees it.
  const showAreaBadge = new Set((roles || []).map((item) => item.area?.id || "")).size > 1;

  return (
    <section>
      {!readOnly && (
        <>
          <h3 className="text-sm font-semibold text-gray-900">{t("user_form_roles_title")}</h3>
          <p className="mt-0.5 text-xs text-gray-500">{t("user_form_roles_hint")}</p>
        </>
      )}

      {!readOnly && managedAreas.length > 0 && (
        <div className="mt-2">
          <IndividualRoleAdd
            companyId={companyId}
            userId={userId}
            managedAreas={managedAreas}
            individualRoleIds={individualRoleIds}
            onChanged={loadOwnRoles}
          />
        </div>
      )}

      <div className={readOnly ? "space-y-4 divide-y divide-zinc-100" : "mt-2 space-y-1.5"}>
        {loadFailed ? (
          <p className="text-sm text-red-700">{t("positions_load_failed")}</p>
        ) : roles === null ? (
          <p className="text-sm text-gray-500">{t("loading")}</p>
        ) : roles.length === 0 ? (
          <p className="text-sm text-gray-400">{t("no_assigned_roles")}</p>
        ) : (
          roles.map((item, idx) => (
            <div key={item.role.id} className={readOnly ? `pt-4 ${idx === 0 ? "pt-0 border-0" : ""}` : undefined}>
              <div className={readOnly ? "flex flex-wrap items-center gap-2" : "flex flex-wrap items-center gap-1.5 text-sm"}>
                {readOnly ? (
                  <h3 className="text-sm font-semibold text-zinc-900">{item.role.name}</h3>
                ) : (
                  <span className="text-gray-900">{item.role.name}</span>
                )}
                {showAreaBadge && item.area && (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                    {item.area.name}
                  </span>
                )}
                <RoleSourceBadges item={item} />
                {!readOnly && item.isIndividual && item.area && managedAreaIds.has(item.area.id) && (
                  <button
                    type="button"
                    className="text-xs font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                    onClick={() => removeIndividual(item)}
                    disabled={removingRoleId === item.role.id}
                  >
                    {t("ws_individual_remove")}
                  </button>
                )}
              </div>
              {readOnly && item.role.description && (
                <p className="text-xs text-zinc-500 mt-0.5">{item.role.description}</p>
              )}
              {renderRoleExtra?.(item)}
            </div>
          ))
        )}
      </div>
    </section>
  );
}
